import React, { useContext, useMemo } from 'react';
import _compact from 'lodash/compact';
import _flatten from 'lodash/flatten';
import makeStyles from '@material-ui/core/styles/makeStyles';
import withSource from 'components/withSource/withSource';
import MappingsContext from 'helpers/MappingsContext';
import Directory from 'types/Directory';
import MappingProps, * as MappingTypes from 'types/Mapping';
import useExistingOrgMappingsInfo from './useExistingOrgMappingsInfo';
import { HOME_URL, DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';
import {
  CSV_SOURCE_TYPE_KEY,
  ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  TL_MATCH_MAPPING_INDEX,
  TL_REF_MAPPING_INDEX,
} from 'pages/Directory/consts';

import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import TableBody from '@material-ui/core/TableBody';
import Link from '@material-ui/core/Link';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import OrgSourceMapping from './OrgSourceMapping';
import ExampleOrgConnect from 'components/Logos/ExampleOrgConnect';
import ValidationStatusIndicator from 'atoms/ValidationStatusIndicator';
import getSourceHeaderCopy from 'components/Mappings/_helpers/getSourceHeaderCopy';

interface Props {
  directory: Directory;
  mappings: MappingProps[];
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps) => void;
  onUpdateCard: (newMappings: MappingProps[]) => void;
}

function OrgMapping({ directory, mappings, onUpdateMapping, onUpdateCard }: Props) {
  const classes = useStyles();
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { source } = mappingsContext;
  const existingMappingsInfo = useExistingOrgMappingsInfo(directory, source.id);
  const { orgMappingsSet, orgMappedToSourceId, orgMappedToSourceName } = existingMappingsInfo;
  const [tlMatchMapping, tfRefMapping] = mappings;
  const isCsvSource = source.type === CSV_SOURCE_TYPE_KEY;
  const validationStatus = useMemo(() => {
    const validationChecks = _compact(_flatten(mappings.map(m => m.validation)));

    if (!validationChecks.length) {
      return <ValidationStatusIndicator status="success" />;
    }

    const { type, longDescriptionHtml } = validationChecks[0];

    return <ValidationStatusIndicator status={type} description={longDescriptionHtml} />;
  }, [mappings]);

  return (
    <>
      <CardHeader
        title={'Set Up Org Chart (Optional)'}
        subheader={
          <>
            {orgMappingsSet ? (
              <div className={classes.subtitle}>
                Your org chart data has already been mapped in source:{' '}
                <Link
                  href={`${HOME_URL}${DIRECTORIES_URL}/${directory.id}${SOURCE_URL}/${orgMappedToSourceId}`}
                  color="secondary"
                >
                  {orgMappedToSourceName}
                </Link>
                {'.'}
              </div>
            ) : (
              <>
                {isCsvSource ? (
                  <div className={classes.description}>
                    To properly create your Org Chart, you need to provide us with information
                    around how a person’s <strong>direct supervisor (manager)</strong> is
                    represented in your CSV file. In the <strong>Supervisor Header</strong>{' '}
                    dropdown, select the column header that points to a person’s manager (ex:
                    “Manager Email”, “Supervisor Employee Id”, etc). In the{' '}
                    <strong>Employee Header</strong> dropdown, select the column header that the
                    supervisor header is referencing on the associated person (ex: “Email”,
                    “Employee Id”, etc).
                  </div>
                ) : (
                  <div className={classes.description}>
                    To properly create your Org Chart, we need to understand how a person’s direct
                    supervisor (manager) is represented in your source data.{' '}
                    <strong>
                      You should never need to change this from the default unless your organization
                      represents managers in an alternate way within your data source.
                    </strong>
                    <br />
                    <br />
                    In the <strong>Supervisor Property</strong> input, select the property that
                    points to a person’s manager. In the <strong>Employee Property</strong>{' '}
                    dropdown, select the property that the supervisor property is referencing on the
                    associated person.
                  </div>
                )}

                {isCsvSource && <ExampleOrgConnect />}
              </>
            )}
          </>
        }
        action={!orgMappingsSet && <div className={classes.validationIcon}>{validationStatus}</div>}
        classes={{
          subheader: classes.subheaderCardHeader,
        }}
      />
      {!orgMappingsSet && (
        <CardContent>
          <Table>
            <TableHead>
              <TableHeadCell>
                {source.type ? getSourceHeaderCopy(source.type) : 'Other Source Property'}
              </TableHeadCell>
              <TableHeadCell>Preview Data</TableHeadCell>
              <TableHeadCell />
            </TableHead>
            <TableBody>
              <OrgSourceMapping
                mappingIndex={TL_REF_MAPPING_INDEX}
                source={source}
                destination={ORG_TEAM_LEADER_MAPPING_REF_KEY}
                mapping={tfRefMapping}
                onUpdateMapping={onUpdateMapping}
                disabled={orgMappingsSet}
              />
              <OrgSourceMapping
                mappingIndex={TL_MATCH_MAPPING_INDEX}
                source={source}
                destination={ORG_TEAM_LEADER_MAPPING_MATCH_KEY}
                mapping={tlMatchMapping}
                onUpdateMapping={onUpdateMapping}
                disabled={orgMappingsSet}
              />
            </TableBody>
          </Table>
        </CardContent>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  subheaderCardHeader: {
    maxWidth: 1200,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  description: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(),
  },
  subtitle: {
    marginTop: theme.spacing(2.125),
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.common.black,
  },
  orgMapCell: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  success: {
    color: theme.palette.success.main,
  },
  validationIcon: {
    marginRight: theme.spacing(2.9),
    marginTop: theme.spacing(),
  },
}));

export default withSource(OrgMapping);
